/**
 * The two buttons that turn a drawing into a round: save, and play.
 *
 * Until now `/editor` could only hand you a `.txt` to copy into
 * `server/src/content/maps/` and restart the server with. These are the calls
 * that remove both steps — `POST /api/admin/maps` stores the file in the bucket
 * and hot-adds it to the running server, and `POST /api/rooms` then mints a room
 * on it like any other map.
 *
 * Separated from `EditorPage.tsx` because every interesting case here is a
 * *response*, and a response is something a test can hand over: the map the
 * server refuses (and the checklist it refuses it with), the session that has
 * expired since the page was opened, and the server with no `ADMIN_PASSWORD` at
 * all. None of those are reachable from a component test.
 *
 * The password is never held in a variable that outlives the call. It goes
 * straight to `POST /api/admin/session`, which answers with an http-only cookie
 * the page cannot read and every later request carries on its own — so "asked
 * once" is the browser's doing rather than a copy of the password kept here.
 */

import type { MapCheck } from '@overcooked/engine'

export type Fetcher = typeof fetch

export type SaveOutcome =
  /** Stored. `enabled` is false for a new map: an admin decides who may see it. */
  | { kind: 'saved'; name: string; enabled: boolean }
  /** No admin session, or it has expired. Ask for the password and try again. */
  | { kind: 'needs-password' }
  /** The engine refused the kitchen. `checks` is the checklist the page shows. */
  | { kind: 'refused'; error: string; checks: MapCheck[]; refusedBecause: string | null }
  /** Something else: no `ADMIN_PASSWORD` on the server, a bucket that is down, no network. */
  | { kind: 'error'; error: string }

interface RefusalBody {
  error?: string
  checks?: MapCheck[]
  refusedBecause?: string | null
}

const message = (body: RefusalBody | null, fallback: string): string =>
  typeof body?.error === 'string' && body.error.length > 0 ? body.error : fallback

async function readJson(res: Response): Promise<RefusalBody | null> {
  try {
    return (await res.json()) as RefusalBody
  } catch {
    return null
  }
}

/** One attempt. `needs-password` is a 401 and nothing else. */
export async function postMap(
  name: string,
  text: string,
  fetcher: Fetcher = fetch,
): Promise<SaveOutcome> {
  let res: Response
  try {
    res = await fetcher('/api/admin/maps', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ name, text }),
    })
  } catch (err) {
    return { kind: 'error', error: `Could not reach the server (${(err as Error).message}). Check your connection.` }
  }

  if (res.status === 401) return { kind: 'needs-password' }
  const body = await readJson(res)
  if (res.ok) {
    const saved = body as unknown as { name?: string; enabled?: boolean } | null
    return { kind: 'saved', name: saved?.name ?? name, enabled: saved?.enabled === true }
  }
  if (res.status === 400 || res.status === 409 || res.status === 413) {
    return {
      kind: 'refused',
      error: message(body, 'The server refused this kitchen.'),
      checks: Array.isArray(body?.checks) ? body.checks : [],
      refusedBecause: body?.refusedBecause ?? null,
    }
  }
  return { kind: 'error', error: message(body, `The server returned an error (${res.status}).`) }
}

/** Trade the password for the cookie. The password goes no further than here. */
export async function logIn(
  password: string,
  fetcher: Fetcher = fetch,
): Promise<{ ok: true } | { ok: false; error: string }> {
  let res: Response
  try {
    res = await fetcher('/api/admin/session', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ password }),
    })
  } catch (err) {
    return { ok: false, error: `Could not reach the server (${(err as Error).message}). Check your connection.` }
  }
  if (res.ok) return { ok: true }
  const body = await readJson(res)
  return { ok: false, error: message(body, `The server returned an error (${res.status}).`) }
}

/**
 * Save, asking for the password only if the server says it needs one.
 *
 * `ask` returns null when somebody closes the prompt, and that is a cancel
 * rather than an error: nothing was stored and nothing went wrong.
 */
export async function saveMap(
  name: string,
  text: string,
  ask: () => string | null | Promise<string | null>,
  fetcher: Fetcher = fetch,
): Promise<SaveOutcome | { kind: 'cancelled' }> {
  const first = await postMap(name, text, fetcher)
  if (first.kind !== 'needs-password') return first

  const password = await ask()
  if (password === null || password === '') return { kind: 'cancelled' }

  const login = await logIn(password, fetcher)
  if (!login.ok) return { kind: 'error', error: login.error }

  // One retry and no loop: a second 401 after a session that was just accepted
  // is a server saying something other than "log in", and asking for the
  // password again would be a page that will not stop asking.
  const second = await postMap(name, text, fetcher)
  if (second.kind === 'needs-password') {
    return { kind: 'error', error: 'The password worked, but the save was still refused. Try again.' }
  }
  return second
}

/**
 * Open a room on a saved map.
 *
 * Deliberately the *same* endpoint the landing page uses, with no admin cookie
 * involved: a map is playable by name from the moment it is stored, whether or
 * not it has been switched on for the lobby. That is how a draft is playtested
 * before anybody else can see it.
 *
 * No invite code is sent. The editor only gets here after a save, which needs
 * the admin session, and `POST /api/rooms` lets a signed-in admin open a room
 * without one — a playtest is not intake, so it is recorded under no batch.
 * `credentials: 'same-origin'` is what carries that session.
 */
export async function openRoomOn(
  name: string,
  fetcher: Fetcher = fetch,
): Promise<{ code: string } | { error: string }> {
  let res: Response
  try {
    res = await fetcher('/api/rooms', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ map: name }),
    })
  } catch (err) {
    return { error: `Could not reach the server (${(err as Error).message}). Check your connection.` }
  }
  const body = (await readJson(res)) as unknown as { code?: string; mapName?: string } | null
  if (!res.ok || typeof body?.code !== 'string') {
    return { error: message(body as RefusalBody, `The server returned an error (${res.status}).`) }
  }
  // `mintRoom` answers with the map it *resolved*, which is the default kitchen
  // when it did not recognise the name — so a room that is not on the saved map
  // is reported rather than opened, which is the failure this whole phase exists
  // to remove.
  if (typeof body.mapName === 'string' && body.mapName !== name) {
    return { error: `The server opened a game on "${body.mapName}" instead. Check that the kitchen is saved.` }
  }
  return { code: body.code }
}
